// ============================================
// FILE: frontend/src/pages/ConnectionChatPage.jsx
// PURPOSE: Chat interface for connected parties with real-time messaging
// FIXED: Polling functions now properly passed to ChatWindow
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useMessages } from '../hooks/useMessages';
import { useConnections } from '../hooks/useConnections';
import { useNotification } from '../hooks/useNotification';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { ChatWindow } from '../components/messaging/ChatWindow';
import { AdminChatMonitor } from '../components/messaging/AdminChatMonitor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react';

export default function ConnectionChatPage() {
  console.log('🎨 [ConnectionChatPage] Page mounted');

  const { connectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  const {
    messages,
    adminChat,
    loading: messagesLoading,
    sendingMessageId,
    loadingChat,
    error: messageError,
    sendMessage,
    fetchMessages,
    adminViewChat,
    startPolling,
    stopPolling
  } = useMessages();

  const {
    currentConnection,
    loading: connectionLoading,
    error: connectionError,
    fetchConnectionDetails
  } = useConnections();

  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authCheckComplete, setAuthCheckComplete] = useState(false);

  // Validation & Authorization
  useEffect(() => {
    console.log('[ConnectionChatPage] 🔐 Running authorization check...', {
      connectionId,
      userId: user?.id,
      userRole: user?.role
    });

    if (!connectionId) {
      console.error('[ConnectionChatPage] ❌ No connection ID provided');
      showError('Error', 'No connection ID provided');
      navigate('/dashboard');
      return;
    }

    if (!user) {
      console.error('[ConnectionChatPage] ❌ User not authenticated');
      navigate(`/login?redirect=/connections/${connectionId}/chat`);
      return;
    }

    const userIsAdmin = user.role === 'ADMIN';
    console.log('[ConnectionChatPage] User role check:', { userIsAdmin });
    setIsAdmin(userIsAdmin);

  }, [connectionId, user, navigate, showError]);

  // Fetch Connection Details
  useEffect(() => {
    if (!connectionId || !user) {
      console.log('[ConnectionChatPage] Skipping connection fetch (no ID or user)');
      return;
    }

    console.log('[ConnectionChatPage] 📥 Fetching connection details...');
    
    fetchConnectionDetails(connectionId)
      .then(() => {
        console.log('[ConnectionChatPage] ✅ Connection details loaded');
      })
      .catch(err => {
        console.error('[ConnectionChatPage] ❌ Failed to fetch connection:', err);
        showError('Error', 'Failed to load connection details');
      });

  }, [connectionId, user, fetchConnectionDetails, showError]);

  // Authorization Check
  useEffect(() => {
    if (!currentConnection || !user) {
      console.log('[ConnectionChatPage] Waiting for connection and user data...');
      return;
    }

    console.log('[ConnectionChatPage] 🔍 Checking user authorization...', {
      userId: user.id,
      landownerId: currentConnection.landownerId,
      investorId: currentConnection.investorId,
      isAdmin
    });

    const authorized =
      isAdmin ||
      currentConnection.landownerId === user.id ||
      currentConnection.investorId === user.id;

    console.log('[ConnectionChatPage] Authorization result:', authorized);

    if (!authorized) {
      console.error('[ConnectionChatPage] ❌ User not authorized for this connection');
      showError('Unauthorized', 'You do not have access to this connection');
      navigate('/dashboard');
      return;
    }

    setIsAuthorized(true);
    setAuthCheckComplete(true);
    console.log('[ConnectionChatPage] ✅ Authorization check complete');

  }, [currentConnection, user, isAdmin, navigate, showError]);

  // Fetch Messages & Start Polling
  useEffect(() => {
    if (!isAuthorized || !connectionId || !authCheckComplete) {
      console.log('[ConnectionChatPage] Skipping message fetch (not authorized or incomplete)');
      return;
    }

    console.log('[ConnectionChatPage] 💬 Loading messages...', { isAdmin });

    if (isAdmin) {
      console.log('[ConnectionChatPage] Admin view - fetching full conversation');
      adminViewChat(connectionId)
        .then(() => {
          console.log('[ConnectionChatPage] ✅ Admin chat loaded, starting polling');
          // Start polling for admin too to keep conversation updated
          startPolling(connectionId, 120000);
        })
        .catch(err => {
          console.error('[ConnectionChatPage] ❌ Failed to fetch admin chat:', err);
        });
    } else {
      console.log('[ConnectionChatPage] User view - fetching messages and starting polling');
      
      fetchMessages(connectionId)
        .then(() => {
          console.log('[ConnectionChatPage] ✅ Messages loaded, starting polling');
          startPolling(connectionId, 3000);
        })
        .catch(err => {
          console.error('[ConnectionChatPage] ❌ Failed to fetch messages:', err);
        });
    }

    return () => {
      console.log('[ConnectionChatPage] 🛑 Stopping polling on unmount');
      stopPolling();
    };
  }, [
    isAuthorized,
    authCheckComplete,
    connectionId,
    isAdmin,
    adminViewChat,
    fetchMessages,
    startPolling,
    stopPolling
  ]);

  // Event Handlers
  const handleSendMessage = useCallback(async (content) => {
    console.log('[ConnectionChatPage] 📤 Sending message...', {
      contentLength: content.length
    });

    if (!content.trim()) {
      console.log('[ConnectionChatPage] Empty message, ignoring');
      return;
    }

    try {
      await sendMessage(connectionId, content);
      console.log('[ConnectionChatPage] ✅ Message sent successfully');
      showSuccess('Success', 'Message sent');

      if (!isAdmin) {
        console.log('[ConnectionChatPage] Refreshing messages after send');
        fetchMessages(connectionId);
      }
    } catch (error) {
      console.error('[ConnectionChatPage] ❌ Send error:', error);
      showError('Error', messageError || 'Failed to send message');
    }
  }, [connectionId, sendMessage, showSuccess, showError, messageError, isAdmin, fetchMessages]);

  const handleLoadMore = useCallback(() => {
    console.log('[ConnectionChatPage] 📜 Loading more messages');
    if (connectionId) {
      fetchMessages(connectionId);
    }
  }, [connectionId, fetchMessages]);

  const handleBack = useCallback(() => {
    console.log('[ConnectionChatPage] ⬅️ Navigating back');
    navigate(isAdmin ? '/admin/connections' : '/dashboard');
  }, [navigate, isAdmin]);

  // Loading State
  if (connectionLoading || (isAdmin && loadingChat && !adminChat)) {
    console.log('[ConnectionChatPage] Loading...');
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading connection...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Error State
  if (!isAuthorized && authCheckComplete) {
    console.log('[ConnectionChatPage] ❌ Not authorized');
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow container py-12">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unauthorized</AlertTitle>
            <AlertDescription>
              You do not have access to this connection.
            </AlertDescription>
          </Alert>
          <Button onClick={handleBack} className="mt-4">
            Go Back
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  if (connectionError || messageError) {
    console.error('[ConnectionChatPage] ❌ Error state:', connectionError || messageError);
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow container py-12">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {connectionError || messageError}
            </AlertDescription>
          </Alert>
          <Button onClick={handleBack} className="mt-4">
            Go Back
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  console.log('[ConnectionChatPage] 🎨 Rendering chat interface', {
    isAdmin,
    messageCount: isAdmin ? adminChat?.messages?.length : messages.length,
    connectionId: currentConnection?.id
  });

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        <div>
          <h1 className="text-3xl font-bold mb-2">
            {isAdmin ? 'Connection Monitor' : 'Message Conversation'}
          </h1>
          <p className="text-muted-foreground">
            {isAdmin
              ? 'Monitor conversation between landowner and investor'
              : 'Send and receive messages with your matched partner'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            {isAdmin ? (
              <AdminChatMonitor
                chat={adminChat}
                loading={loadingChat}
                error={messageError}
              />
            ) : (
              <div className="h-[600px]">
                <ChatWindow
                  connection={{
                    id: currentConnection?.id,
                    currentUserId: user?.id,
                    otherParty: currentConnection?.landowner?.id === user?.id
                      ? currentConnection?.investor
                      : currentConnection?.landowner
                  }}
                  messages={messages}
                  loading={messagesLoading}
                  sending={sendingMessageId === connectionId}
                  onSendMessage={handleSendMessage}
                  onLoadMore={handleLoadMore}
                  onStartPolling={startPolling}
                  onStopPolling={stopPolling}
                />
              </div>
            )}
          </div>

          {currentConnection && (
            <div className="space-y-4">
              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">
                      CONNECTION ID
                    </p>
                    <p className="text-xs font-mono truncate">
                      {currentConnection.id}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">
                      STATUS
                    </p>
                    <Badge
                      variant={
                        currentConnection.status === 'connected' ? 'default' :
                        currentConnection.status === 'deal_completed' ? 'success' :
                        currentConnection.status === 'deal_failed' ? 'destructive' :
                        'secondary'
                      }
                    >
                      {currentConnection.status?.replace(/_/g, ' ').toUpperCase()}
                    </Badge>
                  </div>

                  {currentConnection.matchScore && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-1">
                        MATCH SCORE
                      </p>
                      <p className="text-2xl font-bold text-primary">
                        {currentConnection.matchScore}%
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">
                      CONNECTED
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(currentConnection.connectedAt || currentConnection.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  {isAdmin && (
                    <>
                      <div className="border-t pt-4">
                        <p className="text-xs font-semibold text-muted-foreground mb-2">
                          LANDOWNER
                        </p>
                        <p className="text-sm font-medium">
                          {currentConnection.landowner?.fullName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {currentConnection.landowner?.email}
                        </p>
                      </div>

                      <div className="border-t pt-4">
                        <p className="text-xs font-semibold text-muted-foreground mb-2">
                          INVESTOR
                        </p>
                        <p className="text-sm font-medium">
                          {currentConnection.investor?.fullName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {currentConnection.investor?.email}
                        </p>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>

              {currentConnection.adminNotes && (
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="pt-6">
                    <p className="text-xs font-semibold text-blue-900 mb-2">
                      Admin Notes:
                    </p>
                    <p className="text-xs text-blue-800 whitespace-pre-wrap">
                      {currentConnection.adminNotes}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}